import { contrastRatio } from '../../../../utils/colorContrast'
import {
  PLUGIN_COLOUR_OPTIONS,
  PLUGIN_MIN_HEADER_ACCENT_CONTRAST,
  PLUGIN_TAB_OPTIONS,
} from '../constants'
import type {
  PluginConfig,
  PluginConfigFormValues,
  PluginConfigPayload,
  PluginTab,
  PluginTheme,
} from '../types'

/** Saved settings → the flat form. */
export const toPluginConfigFormValues = (
  config: PluginConfig
): PluginConfigFormValues => ({
  ...config.tabs,
  ...config.theme,
  initialTab: config.initial_tab ?? '',
})

/**
 * Form values → API payload. An opening tab that is no longer shown falls back
 * to "first tab shown", which the API would otherwise refuse.
 */
export const toPluginConfigPayload = (
  values: PluginConfigFormValues
): PluginConfigPayload => {
  const tabs = Object.fromEntries(
    PLUGIN_TAB_OPTIONS.map(({ value }) => [value, values[value]])
  ) as Record<PluginTab, boolean>
  const theme = Object.fromEntries(
    PLUGIN_COLOUR_OPTIONS.map(({ value }) => [
      value,
      values[value].toUpperCase(),
    ])
  ) as Omit<PluginTheme, 'mode'>

  return {
    initial_tab:
      values.initialTab && tabs[values.initialTab] ? values.initialTab : null,
    tabs,
    theme: { ...theme, mode: values.mode },
  }
}

/** The tabs switched on, as select options for the opening tab. */
export const shownPluginTabOptions = (values: PluginConfigFormValues) =>
  PLUGIN_TAB_OPTIONS.filter(({ value }) => values[value])

/**
 * Whether the primary colour is too faint on the header for tab underlines, in
 * which case the plugin uses the header text colour for them instead.
 */
export const isPrimaryHardToSeeOnHeader = (
  values: Pick<PluginConfigFormValues, 'primary' | 'headerBackground'>
): boolean =>
  contrastRatio(values.primary, values.headerBackground) <
  PLUGIN_MIN_HEADER_ACCENT_CONTRAST
